import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOrderOnGetCartOrdersDTO from "../../../models/DTO/IOrderOnGetCartOrdersDTO";
import IOrderGetCartOrdersDTO from "../../../models/DTO/IOrderGetCartOrdersDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import IOrder from "../../../models/IOrder";
import styled from "styled-components";
import OwTable from "../../shared/OwTable/OwTable";
import IOwTableCols from "../../../models/IOwTableCols";
import { EcommerceOrderDetailRow } from "../EcommerceOrderDetailRow/EcommerceOrderDetailRow";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import OwButton from "../../shared/OwButton/OwButton";
import PaginationBar from "../../shared/PaginationBar/PaginationBar";
import { Socket } from "socket.io-client";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import CartService from "@services/CartService";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import CartStatusEnum from "../../../models/CartStatusEnum";
import { IInactiveCartSummary } from "../../../models/DTO/IOnGetInactiveCartsDTO";
import "./EcommerceOrders.scss";

type Props = {};

const OrderSearchDiv = styled.div`
    margin-left: auto;
    display: flex;
    flex-direction: column;
`;

const OrderTableDiv = styled.div`
    margin-top: 1rem;
`;

const INACTIVE_CARTS_POLL_INTERVAL_MS = 5000;

const getInactiveCartDisplay = (status: CartStatusEnum): { iconClass: string; labelKey: string; isError: boolean } => {
    switch (status) {
        case CartStatusEnum.PENDING:
            return { iconClass: "fad fa-spinner fa-spin", labelKey: "pending_cart_processing", isError: false };
        case CartStatusEnum.PAYMENT_PENDING:
            return { iconClass: "fad fa-pause-circle", labelKey: "pending_cart_awaiting_payment", isError: false };
        case CartStatusEnum.FAILED:
            return { iconClass: "fad fa-exclamation-circle", labelKey: "pending_cart_failed", isError: true };
        case CartStatusEnum.PAYMENT_ERRORED:
        default:
            return { iconClass: "fad fa-exclamation-circle", labelKey: "pending_cart_payment_errored", isError: true };
    }
};

export const EcommerceOrders: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);
    const navigate = useNavigate();

    const [orders, setOrders] = useState<IOrder[]>([]);
    const [initialLoad, setInitialLoad] = useState(true);
    const [isDataFetching, setIsDataFetching] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [isReset, setIsReset] = useState(false);
    const [paginationChanged, setPaginationChanged] = useState(false);
    const [totalOrders, setTotalOrders] = useState<number>();
    const [orderSearch, setOrderSearch] = useState("");
    const [poNoSearch, setPoNoSearch] = useState("");
    const [queryString, setQueryString] = useState("");
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [inactiveCarts, setInactiveCarts] = useState<IInactiveCartSummary[]>([]);
    const previousInactiveCountRef = useRef(0);
    const getCartOrdersRef = useRef<() => void>(() => {});

    useEffect(() => {
        socket.on(EndpointsEnum.OrderOnGetCartOrders, onGetCartOrders);

        getCartOrders();

        document.title = "Orderwerks >> Orders";

        return () => {
            socket.removeListener(EndpointsEnum.OrderOnGetCartOrders, onGetCartOrders);
        };
    }, []);

    useEffect(() => {
        getCartOrdersRef.current = getCartOrders;
    });

    const fetchInactiveCarts = async () => {
        try {
            const response = await CartService.getInactiveCarts(
                userModel,
                supplierModel,
                userModel.isSupplierUser ? undefined : vendorModel
            );
            setInactiveCarts(response.carts ?? []);
        } catch (err) {
            console.warn("Failed to fetch inactive carts", err);
        }
    };

    useEffect(() => {
        fetchInactiveCarts();
        const intervalId = setInterval(fetchInactiveCarts, INACTIVE_CARTS_POLL_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, []);

    // When the inactive-cart count shrinks, a cart finished processing — refresh
    // the orders list so the new order shows up.
    useEffect(() => {
        const prev = previousInactiveCountRef.current;
        if (prev > inactiveCarts.length) {
            getCartOrdersRef.current();
        }
        previousInactiveCountRef.current = inactiveCarts.length;
    }, [inactiveCarts.length]);

    const getCartOrders = () => {
        const payload: IOrderGetCartOrdersDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            limit,
            offset,
        };

        if (queryString.length) {
            payload.queryString = queryString;
        }

        socket.emit(EndpointsEnum.OrderGetCartOrders, payload);
    };

    const onGetCartOrders = (data: IOrderOnGetCartOrdersDTO) => {
        setOrders(data.orders);
        setTotalOrders(data.totalOrders);

        window.document.getElementById("mainView")!.scrollTo({ top: 0 });
    };

    useEffect(() => {
        setupQueryString();
    }, [orderSearch, poNoSearch]);

    useEffect(() => {
        if (isReset) {
            search();
        }
    }, [isReset]);

    useEffect(() => {
        if (paginationChanged) {
            search();
        }
    }, [paginationChanged]);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        console.log("handleChange: ", value);

        switch (name) {
            case "order":
                setOrderSearch(value);
                break;
            case "po_no":
                setPoNoSearch(value);
                break;
        }
    };

    const setupQueryString = () => {
        let _queryString = "";

        const queryArr: string[] = [];

        if (orderSearch.length) {
            queryArr.push(`order=${orderSearch}`);
        }

        if (poNoSearch.length) {
            queryArr.push(`po_no=${poNoSearch}`);
        }

        if (queryArr.length) {
            _queryString = "?" + queryArr.join("&");
        }

        setQueryString(_queryString);
    };

    const search = () => {
        getCartOrders();

        setIsReset(false);
        setPaginationChanged(false);
    };

    const reset = () => {
        setOrderSearch("");
        setPoNoSearch("");
        setQueryString("");
        setIsReset(true);
    };

    const searchOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            search();
        }
    };

    const onPaginationChanged = (_offset: number, _limit: number) => {
        setLimit(_limit);
        setOffset(_offset);
        setPaginationChanged(true);
    };

    return (
        <div className={"EcommerceOrders"}>
            <div className={"orders-page"}>
                <div className={"orders-header"}>
                    <OwTitle title="orders" tag={"h3"} className={"orders-title"} />
                    {totalOrders ? (
                        <span className={"orders-count"}>
                            {totalOrders} <OwLabel title={totalOrders === 1 ? "order" : "orders"} noBottomMargin={true} />
                        </span>
                    ) : null}
                </div>

                {inactiveCarts.length > 0 ? (
                    <div className={"orders-pending"}>
                        <OwLabel title={"pending_cart_banner_title"} className={"orders-section-label"} />
                        {inactiveCarts.map((cart) => {
                            const display = getInactiveCartDisplay(cart.status);
                            const total = HelperService.formatCurrency({
                                value: cart.total || 0,
                                currency: "USD",
                                precision: supplierModel.pricing_precision || 2,
                            });
                            return (
                                <button
                                    type={"button"}
                                    key={cart.cartGuid}
                                    className={`orders-pending-cart ${display.isError ? "is-error" : ""}`}
                                    onClick={() => navigate(`/ecomm/inactive-cart/${cart.cartGuid}`)}>
                                    <span className={"orders-pending-cart-icon"}>
                                        <i className={display.iconClass}></i>
                                    </span>
                                    <span className={"orders-pending-cart-text"}>
                                        <OwLabel title={display.labelKey} isBold={true} noBottomMargin={true} />
                                        <span className={"orders-pending-cart-meta"}>
                                            {cart.itemCount}{" "}
                                            <OwLabel title={cart.itemCount === 1 ? "item" : "items"} noBottomMargin={true} />
                                            {!hidePricing ? <> · {total}</> : null}
                                        </span>
                                    </span>
                                    <i className={"fas fa-chevron-right"}></i>
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div className={"orders-search"}>
                    <div className={"orders-search-field"}>
                        <OwLabel title={"order"} className={"orders-section-label"} />
                        <OwFnInput
                            enableDebounce={false}
                            fn={handleChange}
                            name={"order"}
                            value={orderSearch}
                            onKeyDownFn={searchOnEnter}
                        />
                    </div>
                    <div className={"orders-search-field"}>
                        <OwLabel title={"po_no"} className={"orders-section-label"} />
                        <OwFnInput enableDebounce={false} fn={handleChange} name={"po_no"} value={poNoSearch} onKeyDownFn={searchOnEnter} />
                    </div>
                    <div className={"orders-search-actions"}>
                        <OwButton
                            title={"search"}
                            className={"orders-btn-primary"}
                            leftIconClass={"fas fa-search"}
                            fn={search}
                            active={isSearchActive}
                        />
                        <OwButton title={"reset"} className={"orders-btn-secondary"} fn={reset} active={isSearchActive} />
                    </div>
                </div>

                <div className={"orders-list"}>
                    {orders.length === 0 ? (
                        <div className={"orders-empty"}>
                            <i className={"fad fa-receipt"}></i>
                            <OwLabel title="no_orders_found" noBottomMargin={true} />
                        </div>
                    ) : (
                        orders.map((order) => {
                            return <EcommerceOrderDetailRow key={order.guid} order={order} />;
                        })
                    )}
                </div>

                {totalOrders ? (
                    <PaginationBar offset={offset} limit={limit} totalRecords={totalOrders} onPaginationChanged={onPaginationChanged} />
                ) : null}
            </div>
        </div>
    );
};
